import * as React from 'react';

import { NativePluginRenderer, isNativeWidgetConfig } from '@coursera-web/native-widgets';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import WidgetPreviewFrame, {
  LegacyWidgetPreviewFrame,
} from 'bundles/author-widget/components/preview/WidgetPreviewFrame';
import type { Session } from 'bundles/author-widget/components/preview/WidgetPreviewFrame';
import {
  useIsPluginUpdatedPreviewInAuthoringEnabled,
  useIsPluginUpdatedPreviewInCmlEnabled,
} from 'bundles/authoring/featureFlags';
import { isDialogSupported } from 'bundles/widget/utils/isDialogSupported';

type GatedWidgetPreviewFrameProps = {
  session: Session | undefined;
  showPopupButton?: boolean;
  renderCustomContainer?: (onExpand: () => void) => React.ReactNode;
  // Plugin Props
  isComplete?: boolean;
  nextItem?: ItemMetadata;
  markComplete?: () => void;
  markStarted?: () => void;
  // Frame Props
  showWidgetFooter?: (show: boolean) => void;
  widgetContentTitle?: string;
  sessionId?: string;
  headerCss?: string;
};

/**
 * Decides whether to show a native plugin, the updated iframe frame or the legacy iframe frame.
 */
export const GatedWidgetPreviewFrame = ({
  session,
  isComplete,
  markComplete,
  markStarted,
  nextItem,
  showPopupButton = false,
  showWidgetFooter,
  renderCustomContainer,
  widgetContentTitle,
  sessionId,
  headerCss,
}: GatedWidgetPreviewFrameProps) => {
  const isPluginUpdatedPreviewInAuthoringEnabled = useIsPluginUpdatedPreviewInAuthoringEnabled();
  const isPluginUpdatedPreviewInCmlEnabled = useIsPluginUpdatedPreviewInCmlEnabled();

  const isPluginUpdatedPreviewEnabled = isPluginUpdatedPreviewInAuthoringEnabled || isPluginUpdatedPreviewInCmlEnabled;

  if (isNativeWidgetConfig(session?.configuration) && NativePluginRenderer) {
    return (
      <NativePluginRenderer
        config={session.configuration}
        isComplete={isComplete}
        nextItem={nextItem}
        showPopupButton={showPopupButton}
        markComplete={markComplete}
        markStarted={markStarted}
        renderCustomContainer={renderCustomContainer}
      />
    );
  }

  // iFrame plugins
  const Frame = isPluginUpdatedPreviewEnabled && isDialogSupported() ? WidgetPreviewFrame : LegacyWidgetPreviewFrame;

  return (
    <Frame
      session={session}
      showPopupButton={showPopupButton}
      widgetContentTitle={widgetContentTitle}
      sessionId={sessionId}
      headerCss={headerCss}
      showWidgetFooter={showWidgetFooter}
      renderCustomContainer={renderCustomContainer}
    />
  );
};
